import { SearchIcon, XIcon } from 'lucide-react-native';
import { twMerge } from 'tailwind-merge';

import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';

export type SearchFieldProps = {
  value: string;
  onChangeText: (value: string) => void;
  onSubmitEditing?: () => void;
  /** Runs when the clear button is pressed. Without it the button empties the text. */
  onClear?: () => void;
  /** Names the field for a screen reader. A placeholder disappears once there is text. */
  label: string;
  placeholder: string;
  testID?: string;
  /** Classes for the field's outer box. They are merged after the defaults. */
  className?: string;
  /** Swaps the search icon for a spinner and hides the clear button while a search refreshes. */
  busy?: boolean;
};

/**
 * The search box every keyword search in the app is meant to use: an icon, a text field and a
 * clear button that appears once there is text.
 *
 * Autocorrect and automatic capitalization are off on purpose. These boxes take expert names
 * and clinical terms, which the keyboard would otherwise rewrite while they are typed, and
 * the first letter is not capitalized for the reader. A box that needs to capitalize, such as
 * one that searches people by name, is a different control.
 *
 * The clear button is 48 points wide and 44 tall and is labeled "Clear search", which the
 * end-to-end suites look for. The field's own label is the caller's, and the directory's
 * "Search experts" is read by the Maestro Lights-On flow, so change a label only together with
 * the suites that find it.
 */
export function SearchField({
  value,
  onChangeText,
  onSubmitEditing,
  onClear,
  label,
  placeholder,
  testID,
  className,
  busy = false,
}: SearchFieldProps) {
  const { colors } = useColors();

  return (
    <Input className={twMerge('min-w-0 w-full flex-none gap-1 bg-background-0', className)}>
      <InputSlot className="h-full shrink-0 cursor-default pl-3 pr-0 md:pl-4">
        {busy ? (
          <Spinner size="small" />
        ) : (
          <InputIcon as={SearchIcon} size="lg" className="text-typography-500" />
        )}
      </InputSlot>

      <InputField
        testID={testID}
        aria-label={label}
        accessibilityLabel={label}
        className="pl-2 pr-4 placeholder:text-typography-600 md:pl-4"
        style={{ color: colors.typography[900] }}
        placeholder={placeholder}
        value={value}
        onChangeText={onChangeText}
        onSubmitEditing={onSubmitEditing}
        returnKeyType="search"
        autoCapitalize="none"
        autoCorrect={false}
      />

      {value && !busy ? (
        <InputSlot
          className="flex min-h-[44px] min-w-[48px] items-center justify-center pr-2"
          {...getAccessibilityProps({ label: 'Clear search', role: 'button' })}
          onPress={onClear ?? (() => onChangeText(''))}>
          <InputIcon as={XIcon} size="lg" className="text-typography-500" />
        </InputSlot>
      ) : null}
    </Input>
  );
}
